/**
 * Copyright ©2022 Drivepoint
 */

import React, {useEffect, useRef, useState} from "react";
import jwtDecode from "jwt-decode";
import {useNavigate} from "react-router-dom";
import AuthenticationLogo from "@components/AuthenticationLogo/AuthenticationLogo";
import AuthenticationPageSwitch from "@components/AuthenticationPageSwitch/AuthenticationPageSwitch";
import BrandAtmosphere from "@components/BrandAtmosphere/BrandAtmosphere";
import useQueryParams from "@hooks/useQueryParams";
import WebAppServerClient from "@services/clients/WebAppServerClient";
import Firebase from "@services/firebase/Firebase";
import State from "@services/state/State";
import Telemetry from "@services/telemetry/Telemetry";
import Page from "../../Page";
import BainbridgeAuthentication from "../../quickstart/BainbridgeAuthentication/BainbridgeAuthentication";
import "./SignInPage.less";
import "../../quickstart/FirebaseUIAuthentication.css";
import "./SignInPage.css";

const FIREBASE_UI_TITLE_SELECTOR = ".firebaseui-title";

function readFirebaseUiTitle(authenticationContainer: HTMLDivElement | null): string {
  return authenticationContainer?.querySelector<HTMLElement>(FIREBASE_UI_TITLE_SELECTOR)?.textContent?.trim() || "";
}

export default function SignInPage(props: any): any {

  const queryParams = useQueryParams();
  const navigate = useNavigate();
  const isMcpSession = queryParams.get("mcp") === "true" && !!queryParams.get("session_id");
  const mcpCallback = queryParams.get("callback");
  const [mcpSessionCreated, setMcpSessionCreated] = useState<boolean>(false);
  const [authenticationTitle, setAuthenticationTitle] = useState<string>("");
  const authenticationContainerRef = useRef<HTMLDivElement>(null);
  const lastAuthenticationTitleRef = useRef<string>("");

  useEffect(() => {
    if (mcpSessionCreated) { return; }
    const authenticationContainer = authenticationContainerRef.current;
    if (!authenticationContainer) { return; }
    function syncAuthenticationTitle(): void {
      const nextAuthenticationTitle = readFirebaseUiTitle(authenticationContainerRef.current);
      if (nextAuthenticationTitle === lastAuthenticationTitleRef.current) { return; }
      lastAuthenticationTitleRef.current = nextAuthenticationTitle;
      setAuthenticationTitle(nextAuthenticationTitle);
    }
    const authenticationObserver = new MutationObserver(syncAuthenticationTitle);
    authenticationObserver.observe(authenticationContainer, {childList: true, subtree: true, characterData: true});
    syncAuthenticationTitle();
    return () => authenticationObserver.disconnect();
  }, [mcpSessionCreated]);

  useEffect(() => {
    State.set("chrome", "disabled");
    // When returning from an OAuth redirect, skip the sign-out so FirebaseUI (started
    // by BainbridgeAuthentication) can complete sign-in from the pending redirect result.
    if (!Firebase.ui.isPendingRedirect()) {
      Firebase
        .signOut()
        .then(async () => {
          const jwt = queryParams.get("token");
          if (jwt) {
            await clearPasswordReset(jwt);
          } else {
            await attemptAutoSignInWithEmailLink();
          }
        });
    }
    return () => {
      State.set("chrome", "enabled");
    };
  }, []);

  async function clearPasswordReset(jwt: string): Promise<void> {
    try {
      const payload: any = jwtDecode(jwt);
      if (payload.is_reset) {
        await WebAppServerClient.clearPasswordReset({token: jwt});
      }
    } catch (error: any) {
      logger.error(error.message);
    }
  }

  async function attemptAutoSignInWithEmailLink(): Promise<void> {
    const email = localStorage.getItem("bb-auto-sign-in-email");
    localStorage.removeItem("bb-auto-sign-in-email");
    if (email && Firebase.isSignInWithEmailLink(window.location.href)) {
      const user = await Firebase.signInWithEmailLink(email);
      if (user) { return navigate("/"); }
    }
  }

  function signUp(event: any): void {
    event.stopPropagation();
    event.preventDefault();
    navigate("/quickstart");
  }

  async function createSession(excelUser: any): Promise<void> {
    const sessionId = queryParams.get("session_id");
    try {
      await WebAppServerClient.request("/ui/mcp_session", "POST", {session_id: sessionId});
      Telemetry.track("SignInPage MCP Session Created", {sessionId, email: excelUser.email});
      setMcpSessionCreated(true);
      if (mcpCallback) {
        // OAuth flow: continue to the MCP server's callback, which redirects back to the MCP client.
        // small delay so the user sees the confirmation instead of an abrupt redirect:
        setTimeout(() => window.location.replace(mcpCallback), 1500);
        return;
      }
      window.close();
    } catch (error: any) {
      logger.error(error.message);
    }
  }

  async function onSuccess(excelUser: any): Promise<void> {
    Telemetry.track("signin", {email: excelUser.email});
    if (isMcpSession) {
      await createSession(excelUser);
      return;
    }
    if (queryParams.has("cb")) {
      window.location.replace(`/?cb=${queryParams.get("cb")}`);
    } else {
      window.location.replace("/");
    }
  }

  async function onFailure(error: any): Promise<void> {
    logger.debug(error);
  }

  function renderLogin(): any {
    return <AuthenticationPageSwitch prompt="No account yet?" action="Sign up" onClick={signUp} transitionDirection="forward" />;
  }

  const signInTitle = isMcpSession ? "Connect to Drivepoint" : authenticationTitle || "Sign In";

  return <Page>
    <div className="sign-in-page">
      <BrandAtmosphere tone="light" />
      <div className="header">
        <AuthenticationLogo tone="primary" />
        {!isMcpSession && renderLogin()}
      </div>
      {mcpSessionCreated
        ? <div className="phase">
          <h1 className="title">You're signed in</h1>
          <div className="sign-in-page-mcp-session-complete">
            {mcpCallback
              ? "Taking you back to your MCP client…"
              : "You can now close this tab and return to your MCP client."}
          </div>
        </div>
        : <div className="phase">
          <h1 className="title" aria-live="polite">{signInTitle}</h1>
          {isMcpSession &&
            <div className="sign-in-page-mcp-banner">
              An MCP client (such as Claude) is requesting access to your Drivepoint account.
              Sign in to allow it to use Drivepoint on your behalf.
            </div>}
          <div className="bainbridge-authentication-container" ref={authenticationContainerRef}>
            <BainbridgeAuthentication onSuccess={onSuccess} onFailure={onFailure} mode="sign_in" disableSignUp={isMcpSession} />
          </div>
        </div>}
    </div>
  </Page>;

}
